import React, { useEffect, useRef, useState } from "react";

import { isNullOrUndefined, isWithinBounds, useGetFootprintingDairyHerdDetails, useMutateFootprintingDairyHerdDetails, useTranslation } from "utils";

import {
  ENUM_FARM_FOOTPRINTING_DAIRY_COW_BODY_CONDITION,
  ENUM_FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT,
  FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT,
} from "models";

import { Card, Flex, Form, FormInstance, InputNumber, KlimRadioGroup, PageSpin, SelectableImageCard } from "components";

interface DairyGeneralProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const COW_BODY_CONDITIONS = [
  { id: "thin", translationKey: "thin", estimatedWeight: 500 },
  { id: "moderate", translationKey: "moderate", estimatedWeight: 625 },
  { id: "fat", translationKey: "fat", estimatedWeight: 775 },
] as const;

export const HerdConditionForm = ({ primaryDataId, readOnly, farmId, year }: DairyGeneralProps) => {
  const { t } = useTranslation("footprintingDairy");
  const formRef = useRef<FormInstance>(null);

  const [cowWeightKnown, setCowWeightKnown] = useState<ENUM_FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT | null>(null);
  const [averageCowWeightKg, setAverageCowWeightKg] = useState<number | null>(null);
  const [estimatedCowBodyCondition, setEstimatedCowBodyCondition] = useState<ENUM_FARM_FOOTPRINTING_DAIRY_COW_BODY_CONDITION | null>(null);

  const { data: herdDetails, isLoading, isSuccess } = useGetFootprintingDairyHerdDetails(primaryDataId ?? "", { enabled: !!primaryDataId });

  const { mutate: mutateHerdDetails } = useMutateFootprintingDairyHerdDetails(
    farmId,
    year,
    {
      cowWeightKnown,
      averageCowWeightKg,
      estimatedCowBodyCondition,
    },
    primaryDataId
  );

  useEffect(() => {
    if (herdDetails && isSuccess) {
      setCowWeightKnown(herdDetails.cowWeightKnown ?? null);
      setAverageCowWeightKg(herdDetails.averageCowWeightKg ?? null);
      setEstimatedCowBodyCondition(herdDetails.estimatedCowBodyCondition ?? null);

      formRef.current?.setFieldsValue({
        averageCowWeightKg: herdDetails.averageCowWeightKg ?? null,
      });

      const fieldsToValidate = (["cowWeightKnown", "averageCowWeightKg"] as const).some(
        (field) => herdDetails[field] !== null && herdDetails[field] !== undefined
      );

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [herdDetails, isSuccess]);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateHerdDetails();
      }
    },
    [isLoading, mutateHerdDetails, readOnly]
  );

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <div>
      <h1 className="center margin-bottom-200 margin-left-100 margin-right-100">{t("dairyHerdConditionForm.title")}</h1>
      <Form ref={formRef}>
        <Card dataTestId="footprinting-farm-soil-form" className="margin-bottom-100 padding-200">
          <Flex vertical gap={24}>
            <Flex align="center" gap={16} justify="space-between">
              <span style={{ maxWidth: "49%" }}>
                {t("dairyHerdConditionForm.question.cowWeightRadioQuestion")}
                <span className="required-asterisk">*</span>
              </span>
              <KlimRadioGroup
                required
                vertical={false}
                disabled={readOnly}
                type="tertiary"
                size="large"
                dataTestId="footprinting-dairy-herd-cow-weight-radio"
                onChange={({ target }) => {
                  setCowWeightKnown(target.value);
                }}
                value={cowWeightKnown}
                optionType="button"
                options={[FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT.YES, FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT.NO].map((cowWeightKnown) => ({
                  label: t(`dairyHerdConditionForm.cowWeightOptions.${cowWeightKnown}`),
                  value: cowWeightKnown,
                }))}
              />
            </Flex>

            {cowWeightKnown === FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT.YES && (
              <Form.Item>
                <InputNumber
                  disabled={readOnly}
                  name="averageCowWeightKg"
                  dataTestId="footprinting-dairy-herd-cow-weight-input"
                  label={t("dairyHerdConditionForm.question.cowWeightInput")}
                  suffix={t("dairyHerdConditionForm.question.cowWeightSuffix")}
                  value={averageCowWeightKg}
                  initialValue={averageCowWeightKg}
                  onChange={(v) => setAverageCowWeightKg(v)}
                  validateFirst
                  rules={[
                    { required: true, message: t("dairyHerdConditionForm.error.cowWeightAmount") },
                    { type: "number" },
                    {
                      validator: (_: unknown, v: number) => {
                        if (isNullOrUndefined(v) || !isWithinBounds(v, 50, 1500)) {
                          return Promise.reject(new Error(t("dairyHerdConditionForm.error.cowWeightAmount")));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                />
              </Form.Item>
            )}
          </Flex>
        </Card>

        {cowWeightKnown === FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT.NO && (
          <Card className="margin-bottom-100 padding-200">
            <Flex vertical gap={24}>
              <Flex vertical>
                <Flex vertical className="cow-body-condition-container">
                  <h4 className="margin-bottom-100">{t("dairyHerdConditionForm.estimateWeightQuestion")}</h4>
                  {COW_BODY_CONDITIONS.map((condition) => (
                    <SelectableImageCard
                      key={condition.id}
                      image={{
                        imageSrc: `https://klimpublic.s3.eu-central-1.amazonaws.com/cow-body-${condition.translationKey}.webp`,
                        imageAlt: `cow-body-${condition.translationKey}`,
                        imageSize: 144,
                      }}
                      selectionMode="single"
                      title={t(`dairyHerdConditionForm.bodyCondition.${condition.translationKey}.title`)}
                      description={t(`dairyHerdConditionForm.bodyCondition.${condition.translationKey}.description`)}
                      largeDescription
                      onChange={() => {
                        const conditionId = condition.id as ENUM_FARM_FOOTPRINTING_DAIRY_COW_BODY_CONDITION;
                        setEstimatedCowBodyCondition(conditionId);

                        setAverageCowWeightKg(condition.estimatedWeight);
                        formRef.current?.setFieldValue("averageCowWeightKg", condition.estimatedWeight);
                      }}
                      checked={estimatedCowBodyCondition === condition.id}
                      disabled={readOnly}
                    />
                  ))}
                </Flex>

                <Form.Item>
                  <InputNumber
                    className="margin-top-150"
                    disabled={readOnly}
                    name="averageCowWeightKg"
                    dataTestId="footprinting-dairy-herd-cow-weight-input"
                    label={t("dairyHerdConditionForm.question.cowWeightInput")}
                    suffix={t("dairyHerdConditionForm.question.cowWeightSuffix")}
                    value={averageCowWeightKg}
                    initialValue={averageCowWeightKg}
                    onChange={(v) => setAverageCowWeightKg(v)}
                    validateFirst
                    rules={[
                      { required: true, message: t("dairyHerdConditionForm.error.cowWeightAmount") },
                      { type: "number" },
                      {
                        validator: (_: unknown, v: number) => {
                          if (isNullOrUndefined(v) || !isWithinBounds(v, 50, 1500)) {
                            return Promise.reject(new Error(t("dairyHerdConditionForm.error.cowWeightAmount")));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  />
                </Form.Item>
                <p className="paragraph-s grey-600-text margin-top-25 margin-bottom-0">{t("dairyHerdConditionForm.calculationMessage")}</p>
              </Flex>
            </Flex>
          </Card>
        )}
      </Form>
    </div>
  );
};
